import { useParams } from 'react-router-dom';
import { logError } from 'setup/logger/logError';
import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';

export function getParamWithColon(param: UrlParamName): string {
  return `:${param}`;
}

export enum UrlParamName {
  CUSTOMIZED_FIELD_NAME = 'customizedFieldName',
  CHART_ID = 'chartId',
  USER_ID = 'userId',
  CONTRACT_ID = 'contractId',
  DOCUMENT_ID = 'documentId',
  COMPANY_ID = 'companyId',
  SITE_ID = 'siteId',
  ISSUE_ID = 'issueId',
  EVENT_ID = 'eventId',
  PROJECT_ID = 'projectId',
  ORGANIZATION_ID = 'organizationId',
  INSPECTION_ID = 'inspectionId',
  INSPECTION_TEMPLATE_ID = 'inspectionTemplateId',
  LEVEL_ID = 'levelId',
  REPORT_ID = 'reportId',
  FILE_ID = 'fileId',
  DIRECTORY_ID = 'directoryId',
  PROTOCOL_ITEM_ID = 'protocolItemId',
  CHECKLIST_ITEM_ID = 'checklistItemId',
  LINKED_DOCUMENTATION_VERSION_ID = 'linkedDocumentationVersionId',
  INCOMING_EMAIL_MESSAGE_ID = 'incomingEmailMessageId',
  LINKED_DOCUMENTATION_ID = 'linkedDocumentationId',
  DECISION_ID = 'decisionId',
  WORKFLOW_ID = 'workflowId',
  SECTION_ID = 'sectionId',
}

export interface AllowedUrlParams {
  [UrlParamName.CUSTOMIZED_FIELD_NAME]: EditableFieldValuesEntityName;
  [UrlParamName.CHART_ID]: string;
  [UrlParamName.USER_ID]: string;
  [UrlParamName.CONTRACT_ID]: string;
  [UrlParamName.DOCUMENT_ID]: string;
  [UrlParamName.COMPANY_ID]: string;
  [UrlParamName.SITE_ID]: string;
  [UrlParamName.ISSUE_ID]: string;
  [UrlParamName.EVENT_ID]: string;
  [UrlParamName.PROJECT_ID]: string;
  [UrlParamName.ORGANIZATION_ID]: string;
  [UrlParamName.INSPECTION_ID]: string;
  [UrlParamName.INSPECTION_TEMPLATE_ID]: string;
  [UrlParamName.LEVEL_ID]: string;
  [UrlParamName.REPORT_ID]: string;
  [UrlParamName.FILE_ID]: string;
  [UrlParamName.DIRECTORY_ID]: string;
  [UrlParamName.PROTOCOL_ITEM_ID]: string;
  [UrlParamName.CHECKLIST_ITEM_ID]: string;
  [UrlParamName.LINKED_DOCUMENTATION_VERSION_ID]: string;
  [UrlParamName.INCOMING_EMAIL_MESSAGE_ID]: string;
  [UrlParamName.LINKED_DOCUMENTATION_ID]: string;
  [UrlParamName.DECISION_ID]: string;
  [UrlParamName.WORKFLOW_ID]: string;
  [UrlParamName.SECTION_ID]: string;
}

export function useAppUrlParams<T extends keyof AllowedUrlParams>(
  paramName: T,
  isRequired = false
): AllowedUrlParams[T] {
  const params = useParams<Record<string, string>>();

  if (!params[paramName] && isRequired) {
    const msg = `Missing required URL parameter: ${paramName}`;
    logError(msg);
    throw new Error(msg);
  }

  return params[paramName] as AllowedUrlParams[T];
}
